import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    CircularProgress,
    Alert,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    InputAdornment,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Description,
    Person,
    Receipt,
    AutoAwesome,
    Search as SearchIcon,
} from '@mui/icons-material';
import { consultationService, ConsultationResponse } from '../../services/consultationService';
import { PageHeader } from '../../components/common/PageHeader';

export const HistoryPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [consultations, setConsultations] = useState<ConsultationResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [consultationToDelete, setConsultationToDelete] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadConsultations();
    }, []);

    const loadConsultations = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await consultationService.getAllConsultations();
            setConsultations(data);
        } catch (err: any) {
            setError(t('common.loadError'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleConsultationClick = (id: string) => {
        navigate(`/consultation/${id}`);
    };



    const handleDeleteClick = (event: React.MouseEvent, id: string) => {
        event.stopPropagation();
        setConsultationToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!consultationToDelete) return;

        try {
            setDeleting(true);
            await consultationService.deleteConsultation(consultationToDelete);
            setDeleteDialogOpen(false);
            setConsultationToDelete(null);
            // Reload consultations
            await loadConsultations();
        } catch (err: any) {
            setError(t('history.deleteError'));
            console.error(err);
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setConsultationToDelete(null);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const hasContent = (text: string | null | undefined): boolean => {
        return Boolean(text && text.trim().length > 0);
    };

    const filteredConsultations = searchQuery.trim()
        ? consultations.filter((c) =>
              (c.patient_name || '').toLowerCase().includes(searchQuery.trim().toLowerCase())
          )
        : consultations;



    return (
        <Box>
            <PageHeader
                title={t('navigation.history')}
            />

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Card>
                {!loading && consultations.length > 0 && (
                    <Box sx={{ p: 2 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder={t('history.searchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                sx: { height: 40, alignItems: 'center' },
                            }}
                        />
                    </Box>
                )}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : consultations.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                            {t('history.noConsultations')}
                        </Typography>
                    </Box>
                ) : filteredConsultations.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                            {t('history.noResults')}
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small" sx={{ '& .MuiTableCell-root': { px: { xs: 1, sm: 2 } } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('common.date')}</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t('history.species')}</TableCell>
                                    <TableCell>{t('history.animalName')}</TableCell>
                                    <TableCell align="center">
                                        {t('history.documents')}
                                    </TableCell>
                                    <TableCell align="right">{t('common.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredConsultations.map((consultation) => (
                                    <TableRow
                                        key={consultation.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleConsultationClick(consultation.id)}
                                    >
                                        <TableCell>{formatDate(consultation.created_at)}</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Typography variant="body2">
                                                {consultation.patient_species || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {consultation.patient_name || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: { xs: 0.5, sm: 2 } }}>
                                                {/* Compte Rendu */}
                                                <Tooltip title={t('history.compteRendu')}>
                                                    <Description
                                                        sx={{
                                                            color: hasContent(consultation.corrected_transcription)
                                                                ? 'primary.main'
                                                                : 'action.disabled',
                                                        }}
                                                    />
                                                </Tooltip>

                                                {/* Client Report */}
                                                <Tooltip title={t('history.clientReport')}>
                                                    <Person
                                                        sx={{
                                                            color: hasContent(consultation.client_report)
                                                                ? 'secondary.main'
                                                                : 'action.disabled',
                                                        }}
                                                    />
                                                </Tooltip>

                                                {/* Prescription */}
                                                <Tooltip title={t('history.prescription')}>
                                                    <Receipt
                                                        sx={{
                                                            color: hasContent(consultation.prescription)
                                                                ? 'success.main'
                                                                : 'action.disabled',
                                                        }}
                                                    />
                                                </Tooltip>

                                                {/* AI Analysis */}
                                                <Tooltip title={t('history.aiAnalysis')}>
                                                    <AutoAwesome
                                                        sx={{
                                                            color: consultation.ai_suggestions
                                                                ? 'warning.main'
                                                                : 'action.disabled',
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title={t('common.delete')}>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => handleDeleteClick(e, consultation.id)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Card>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
            >
                <DialogTitle>{t('history.deleteConfirmTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('history.deleteConfirmMessage')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={deleting}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                    >
                        {deleting ? <CircularProgress size={20} /> : t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
